// frontend/src/pages/ChartOfAccounts/coaApi.js
import axios from "axios";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getId = (value) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value._id || value.id || null;
  }

  return value;
};

const canonicalMainType = (value) => {
  if (value === "Capital") return "Equity";
  if (value === "Revenue") return "Income";
  return value || "";
};

const createApiError = (error) => {
  const responseData = error.response?.data;

  const fieldErrors = Array.isArray(responseData?.errors)
    ? responseData.errors
        .map((item) => item?.message)
        .filter(Boolean)
        .join(", ")
    : "";

  const message =
    responseData?.message ||
    responseData?.error ||
    fieldErrors ||
    error.message ||
    "Request failed";

  const apiError = new Error(message);
  apiError.status = error.response?.status;
  apiError.details = responseData;

  return apiError;
};

const runRequest = async (requestFactory) => {
  try {
    return await requestFactory();
  } catch (error) {
    throw createApiError(error);
  }
};

const extractAccounts = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.accounts)) {
    return payload.data.accounts;
  }
  if (Array.isArray(payload?.accounts)) return payload.accounts;
  return [];
};

const getAccountingType = (account) => {
  if (account?.accountingType) {
    return account.accountingType;
  }

  if (
    account?.accounting_type &&
    typeof account.accounting_type === "object"
  ) {
    return account.accounting_type;
  }

  return {};
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getDirectSignedBalance = (account = {}) => {
  if (Number.isFinite(Number(account.own_signed_balance))) {
    return Number(account.own_signed_balance);
  }

  if (Number.isFinite(Number(account.signed_balance))) {
    return Number(account.signed_balance);
  }

  const openingDebit = toFiniteNumber(account.opening_debit);
  const openingCredit = toFiniteNumber(account.opening_credit);
  const debitTotal = toFiniteNumber(account.debit_total);
  const creditTotal = toFiniteNumber(account.credit_total);
  const hasLedgerValues =
    openingDebit !== 0 ||
    openingCredit !== 0 ||
    debitTotal !== 0 ||
    creditTotal !== 0;

  if (hasLedgerValues) {
    return openingDebit + debitTotal - openingCredit - creditTotal;
  }

  return toFiniteNumber(account.balance);
};

const buildTree = (accounts) => {
  const accountMap = new Map();
  const roots = [];

  accounts.forEach((account) => {
    accountMap.set(String(account._id), {
      ...account,
      children: [],
      own_signed_balance: getDirectSignedBalance(account),
    });
  });

  accountMap.forEach((account) => {
    const parentId =
      getId(account.parent_account) ||
      getId(account.parentAccount);

    if (parentId && accountMap.has(String(parentId))) {
      accountMap.get(String(parentId)).children.push(account);
    } else {
      roots.push(account);
    }
  });

  const sortAccounts = (items) => {
    items.sort((first, second) => {
      const sortDifference =
        Number(first.sort_order || 0) -
        Number(second.sort_order || 0);

      if (sortDifference !== 0) return sortDifference;

      return String(first.account_code || "").localeCompare(
        String(second.account_code || ""),
        undefined,
        { numeric: true },
      );
    });
  };

  const calculateNode = (account) => {
    sortAccounts(account.children);

    const visibleChildrenBalance = account.children.reduce(
      (total, child) => total + calculateNode(child),
      0,
    );

    const hasChildren = Boolean(
      account.has_children === true || account.children.length > 0,
    );

    const backendChildrenBalance = Number(account.children_balance);
    const childrenBalance = Number.isFinite(backendChildrenBalance)
      ? backendChildrenBalance
      : visibleChildrenBalance;

    const backendConsolidatedBalance = Number(
      account.consolidated_balance,
    );
    const consolidatedBalance = Number.isFinite(
      backendConsolidatedBalance,
    )
      ? backendConsolidatedBalance
      : account.own_signed_balance + childrenBalance;

    const effectiveControl = Boolean(
      account.is_effective_control_account === true ||
        account.is_control_account === true ||
        hasChildren,
    );

    const effectivePosting = Boolean(
      account.status === "active" &&
        !effectiveControl &&
        (account.effective_allow_manual_posting === true ||
          account.allow_manual_posting === true),
    );

    account.has_children = hasChildren;
    account.children_balance = childrenBalance;
    account.consolidated_balance = consolidatedBalance;
    account.is_effective_control_account = effectiveControl;
    account.effective_allow_manual_posting = effectivePosting;

    return consolidatedBalance;
  };

  sortAccounts(roots);
  roots.forEach(calculateNode);

  return roots;
};

const filterAccounts = (
  accounts,
  {
    search = "",
    status = "active",
    main_type = "",
  } = {},
) => {
  const searchValue = String(search).trim().toLowerCase();

  return accounts.filter((account) => {
    if (status !== "all" && account.status !== status) {
      return false;
    }

    const accountingType = getAccountingType(account);
    const accountMainType = canonicalMainType(
      accountingType.type || account.type,
    );

    if (main_type && accountMainType !== main_type) {
      return false;
    }

    if (searchValue) {
      const searchableText = [
        account.account_code,
        account.account_name,
        account.description,
        accountMainType,
        accountingType.sub_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchableText.includes(searchValue)) {
        return false;
      }
    }

    return true;
  });
};

const calculateStats = (accounts) => {
  const stats = {
    total: accounts.length,
    active: 0,
    inactive: 0,
    posting: 0,
    control: 0,
    protected: 0,
    by_main_type: {
      Asset: 0,
      Liability: 0,
      Equity: 0,
      Income: 0,
      Expense: 0,
    },
  };

  const parentIds = new Set(
    accounts
      .map(
        (account) =>
          getId(account.parent_account) || getId(account.parentAccount),
      )
      .filter(Boolean)
      .map(String),
  );

  accounts.forEach((account) => {
    if (account.status === "active") stats.active += 1;
    if (account.status === "inactive") stats.inactive += 1;

    const hasChildren = Boolean(
      account.has_children === true || parentIds.has(String(account._id)),
    );
    const effectiveControl = Boolean(
      account.is_effective_control_account === true ||
        account.is_control_account === true ||
        hasChildren,
    );
    const effectivePosting = Boolean(
      account.status === "active" &&
        !effectiveControl &&
        (account.effective_allow_manual_posting === true ||
          account.allow_manual_posting === true),
    );

    if (effectiveControl) stats.control += 1;
    if (effectivePosting) stats.posting += 1;

    if (account.is_system_account === true || account.is_locked === true) {
      stats.protected += 1;
    }

    const mainType = canonicalMainType(
      getAccountingType(account).type || account.type,
    );

    if (
      mainType &&
      Object.prototype.hasOwnProperty.call(stats.by_main_type, mainType)
    ) {
      stats.by_main_type[mainType] += 1;
    }
  });

  return stats;
};

const normalizePayload = (payload = {}) => {
  const accountingTypeId =
    getId(payload.accounting_type) ||
    getId(payload.accounting_type_id);

  const parentAccountId =
    getId(payload.parent_account) ||
    getId(payload.parent_account_id);

  const companyId = getId(payload.company_id);
  const currencyId = getId(payload.currency);
  const paymentTypeId = getId(payload.paymentstype);

  const openingDebit = Math.max(
    Number(payload.opening_debit || 0),
    0,
  );

  const openingCredit = Math.max(
    Number(payload.opening_credit || 0),
    0,
  );

  const isControlAccount = Boolean(
    payload.is_control_account,
  );

  const allowManualPosting = isControlAccount
    ? false
    : Boolean(
        payload.allow_manual_posting ??
          payload.allow_posting ??
          true,
      );

  return {
    account_code: String(payload.account_code || "")
      .trim()
      .toUpperCase(),

    account_name: String(payload.account_name || "").trim(),

    // New schema field plus alias for any temporary controller code.
    accounting_type: accountingTypeId,
    accounting_type_id: accountingTypeId,

    parent_account: parentAccountId || null,
    parent_account_id: parentAccountId || null,

    // Older account controllers may still validate the main type.
    type: payload.type || undefined,

    description: String(payload.description || "").trim(),

    currency: currencyId || null,
    currency_code: String(payload.currency_code || "USD")
      .trim()
      .toUpperCase(),

    is_control_account: isControlAccount,

    allow_manual_posting: allowManualPosting,
    allow_posting: allowManualPosting,

    sort_order: Number(payload.sort_order || 0),

    opening_debit: openingDebit,
    opening_credit: openingCredit,

    // Current IMS services may still read the cached balance field.
    balance:
      payload.balance !== undefined
        ? Number(payload.balance || 0)
        : openingDebit - openingCredit,

    paymentstype: paymentTypeId || null,

    company_id: companyId || undefined,

    ...(payload.status ? { status: payload.status } : {}),
  };
};

const fetchAllAccounts = async ({
  company_id = "",
  status = "all",
} = {}) => {
  const params = {
    page: 1,
    limit: 500,
    status,
  };

  if (company_id) {
    params.company_id =
      getId(company_id);
  }

  const response =
    await runRequest(() =>
      axios.get(`${url}/api/account`, {
        params,
        headers: getAuthHeaders(),
      }),
    );

  return extractAccounts(
    response.data,
  );
};

export const coaApi = {
  // ⭐ UPDATED: forward force_currency_update and currency_code so the
  // backend can rewrite the protected accounts' currency on repair.
  async ensureInventorySetup({
    company_id = "",
    cleanup_legacy = false,
    force_currency_update = false,
    currency_code = "",
  } = {}) {
    const payload = {
      cleanup_legacy: Boolean(cleanup_legacy),
      force_currency_update: Boolean(force_currency_update),
      ...(company_id ? { company_id: getId(company_id) } : {}),
      ...(currency_code
        ? { currency_code: String(currency_code).trim().toUpperCase() }
        : {}),
    };

    const response = await runRequest(() =>
      axios.post(`${url}/api/account/setup-inventory`, payload, {
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async getNextCode({
    company_id = "",
    parent_account = "",
    accounting_type = "",
  } = {}) {
    const response = await runRequest(() =>
      axios.get(`${url}/api/account/next-code`, {
        params: {
          ...(company_id ? { company_id: getId(company_id) } : {}),
          parent_account: getId(parent_account),
          accounting_type: getId(accounting_type),
        },
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async getTree(filters = {}) {
    const accounts = await fetchAllAccounts({
      company_id: filters.company_id,
      status: filters.status,
    });
  
    const filteredAccounts =
      filterAccounts(accounts, filters);
  
    return {
      success: true,
      data: buildTree(filteredAccounts),
    };
  },

  async getStats(filters = {}) {
    const accounts =
      await fetchAllAccounts({
        company_id:
          filters.company_id,
        status: "all",
      });
  
    return {
      success: true,
      data:
        calculateStats(accounts),
    };
  },

  async getAccountingTypes(params = {}) {
    const response = await runRequest(() =>
      axios.get(`${url}/api/accounting-types/dropdown`, {
        params: {
          ...params,
          ...(params.company_id
            ? { company_id: getId(params.company_id) }
            : {}),
        },
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async getAccountDropdown(params = {}) {
    const response = await runRequest(() =>
      axios.get(`${url}/api/account/dropdown`, {
        params: {
          ...params,
          ...(params.company_id
            ? { company_id: getId(params.company_id) }
            : {}),
        },
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async getCompanies() {
    const response = await runRequest(() =>
      axios.get(`${url}/api/company`, {
        headers: getAuthHeaders(),
      })
    );
    return response.data;
  },

  async getCurrencies(params = {}) {
    const response = await runRequest(() =>
      axios.get(`${url}/api/currency`, {
        params,
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async create(payload) {
    const normalizedPayload = normalizePayload(payload);

    try {
      const response = await axios.post(
        `${url}/api/account`,
        normalizedPayload,
        { headers: getAuthHeaders() },
      );
  
      return response.data;
    } catch (error) {
  
      const backendErrors =
        Array.isArray(
          error.response?.data?.errors,
        )
          ? error.response.data.errors
              .map(
                (item) =>
                  item.message ||
                  item.msg,
              )
              .filter(Boolean)
              .join(", ")
          : "";
  
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          backendErrors ||
          error.message ||
          "Failed to create account",
      );
    }
  },

  async update(id, payload) {
    const response = await runRequest(() =>
      axios.put(`${url}/api/account/${id}`, normalizePayload(payload), {
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async changeStatus(id, status) {
    const response = await runRequest(() =>
      axios.put(`${url}/api/account/${id}`, { status }, {
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },

  async remove(id) {
    const response = await runRequest(() =>
      axios.delete(`${url}/api/account/${id}`, {
        headers: getAuthHeaders(),
      }),
    );

    return response.data;
  },
};